import { useShallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import { CustomForm } from "mobily-ui-library";
import { useGlobalStore } from "../stores/globalStore";
import { useCheckoutStore } from "../stores/checkoutStore";
import { useJourneyOrchestrator } from "../hooks/useJourneyOrchestrator";
import "./RequestDetails.scss";

const NAME_PATTERN = /^[\u0600-\u06FFa-zA-Z ]+$/;

export const RequestDetails = () => {

    const { t } = useTranslation();

    const { lang, formState, setFormState } = useGlobalStore(
        useShallow((state) => ({
            lang: state.lang,
            formState: state.formState,
            setFormState: state.setFormState,
        }))
    );
    const { capturedData, currentSection, currentStep } = useCheckoutStore(
        useShallow((state) => ({
            capturedData: state.capturedData,
            currentSection: state.currentSection,
            currentStep: state.currentStep,
        }))
    );

    const handleStepSubmit = useJourneyOrchestrator();

    const onSubmit = async (values: any) => {
        const existingSection = capturedData.find((s) => s.sectionId === currentSection?.sectionId);
        const firstName = values.firstName?.trim();
        const lastName = values.lastName?.trim();
        const emailAddress = values.emailAddress?.trim();
        const contactNumber = values.contactNumber?.trim();

        const newStep = {
            sequence: currentStep?.sequence,
            stepId: currentStep?.stepId,
            offerContainerId: currentStep?.offerContainerId,
            fields: [
                { name: "First Name", value: firstName },
                { name: "Middle Name", value: null },
                { name: "Last Name", value: lastName },
                { name: "Email Address", value: emailAddress },
                { name: "Contact Number", value: `5${contactNumber}` },
            ],
            options: [
                {
                    name: "Communication language",
                    value: lang === "ar" ? "AR" : "EN"
                }
            ]
        };

        const requestDetailsSection = {
            sectionId: currentSection?.sectionId,
            steps: [
                ...(existingSection?.steps ?? []).filter((s: any) => s.stepId !== currentStep?.stepId),
                newStep,
            ],
        };

        setFormState({
            firstName: firstName,
            lastName: lastName,
            emailAddress: emailAddress,
            contactNumber: contactNumber,
        });

        await handleStepSubmit({ sectionData: requestDetailsSection });
    }

    const rtl = lang === 'ar';

    return (
        <CustomForm
            title={t('enter_Your_Details')}
            fields={[
                {
                    id: "firstName",
                    name: "firstName",
                    fieldType: "input",
                    headingleft: t("first_Name"),
                    placeholder: t("enter_First_Name"),
                    type: "text",
                    rtl,
                    colSpanSm: 24,
                    colSpanMd: 24,
                    colSpanLg: 24,
                    rules: [
                        {
                            validator: (_ : any, value : any) => {
                                const trimmedValue = value?.trim();
                                if (!trimmedValue) {
                                    return Promise.reject(new Error(t("first_Name_Error")));
                                }
                                if(value.length > 45) {
                                    return Promise.reject(new Error(t("name_Max_Length")));
                                }
                                if (!NAME_PATTERN.test(value)) {
                                    return Promise.reject(new Error(t("name_Invalid_Characters")));
                                }
                                return Promise.resolve();
                            }
                        },
                        {
                            required: true,
                            message: ""
                        }
                    ]
                },
                {
                    id: "lastName",
                    name: "lastName",
                    fieldType: "input",
                    headingleft: t("last_Name"),
                    placeholder: t("enter_Last_Name"),
                    type: "text",
                    rtl,
                    colSpanSm: 24,
                    colSpanMd: 24,
                    colSpanLg: 24,
                    rules: [
                        {
                            validator: (_ : any, value : any) => {
                                const trimmedValue = value?.trim();

                                if (!trimmedValue) {
                                    return Promise.reject(new Error(t("last_Name_Error")));
                                }

                                if(value.length > 45) {
                                    return Promise.reject(new Error(t("name_Max_Length")));
                                }
                                if (!NAME_PATTERN.test(value)) {
                                    return Promise.reject(new Error(t("name_Invalid_Characters")));
                                }
                                return Promise.resolve();
                            }
                        },
                        {
                            required: true,
                            message: ""
                        }
                    ]
                },
                {
                    id: "emailAddress",
                    name: "emailAddress",
                    fieldType: "input",
                    headingleft: t("email_Address"),
                    placeholder: t("enter_Email_Address"),
                    type: "email",
                    rtl,
                    colSpanSm: 24,
                    colSpanMd: 24,
                    colSpanLg: 24,
                    rules: [
                        { required: true, message: t("email_Error") },
                        { type: "email", message: t("email_Error") }
                    ]
                },
                {
                    id: "contactNumber",
                    name: "contactNumber",
                    fieldType: "input",
                    headingleft: t("contact_Number"),
                    placeholder: "xxxxxxxx",
                    type: "text",
                    prefix: "05",
                    prefixColor: "",
                    isSpecialFormatted: true,
                    format: "########",
                    formFieldClassName: "input-ltr",
                    rtl,
                    colSpanSm: 24,
                    colSpanMd: 24,
                    colSpanLg: 24,
                    rules: [
                        { required: true, message: t("contact_Number_Error") },
                        { pattern: /^\d{8}$/, message: t("contact_Number_Min_Length") }
                    ]
                },
            ]}
            footerFields={[
                {
                    id: "submitButton",
                    name: "submitButton",
                    fieldType: "button",
                    primary: true,
                    variant: 'default',
                    children: t("continueBtn"),
                    htmlType: 'submit',
                    rtl,
                    formFieldClassName: "nextButton",
                }
            ]}
            footerProps={{
                justify: "center",
                align: "middle",
            }}
            rtl={rtl}
            initialValues={formState}
            onFinish={onSubmit}
            gutter={[24, 28]}
            rootClassName="requestDetailsForm"
        />
    )

}